import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const services = [
  { id: 'land', label: 'Land for Sale', route: '/land', icon: '🌍', subs: ['Residential Plots', 'Agricultural Land', 'Commercial Land'] },
  { id: 'houses-sale', label: 'Houses for Sale', route: '/houses-for-sale', icon: '🏠', subs: ['Apartments', 'Bungalows', 'Mansions', 'Townhouses'] },
  { id: 'houses-rent', label: 'Houses for Rent', route: '/houses-for-rent', icon: '🔑', subs: ['Studio Apartments', '1-3 Bedrooms'] },
  { id: 'cars', label: 'Cars', route: '/cars', icon: '🚗', subs: ['Sedans', 'SUVs', 'Trucks', 'Electric Vehicles'] },
  { id: 'courier', label: 'Courier Services', route: null, icon: '📦', subs: ['Coming Soon'] },
];

const useCountUp = (target: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const timer = setInterval(() => {
          const elapsed = Date.now() - start;
          const progress = Math.min(elapsed / duration, 1);
          setCount(Math.floor(progress * target));
          if (progress === 1) clearInterval(timer);
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
};

const StatCard = ({ label, target, suffix = '+' }: { label: string; target: number; suffix?: string }) => {
  const { count, ref } = useCountUp(target);
  return (
    <div ref={ref} style={{ padding: '40px 32px', background: 'var(--dark-2)', textAlign: 'center' }}>
      <p className="serif gold-text" style={{ fontSize: '48px', fontWeight: 300, lineHeight: 1 }}>{count}{suffix}</p>
      <p style={{ color: 'var(--text-muted)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '8px' }}>{label}</p>
    </div>
  );
};

const HomePage = () => {
  const [active, setActive] = useState<string | null>(null);
  const [centerClicked, setCenterClicked] = useState(false);
  const [stats, setStats] = useState({ rentals: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      const { count: rentalCount } = await supabase.from('rentals').select('*', { count: 'exact', head: true });
      setStats({ rentals: rentalCount || 0 });
    };
    fetchStats();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)' }}>
      <style>{`
        .glass-btn {
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(201,168,76,0.4);
          color: #C9A84C;
          padding: 16px 48px;
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 8px 32px rgba(201,168,76,0.15), inset 0 1px 0 rgba(255,255,255,0.1);
        }
        .glass-btn:hover {
          background: rgba(201,168,76,0.1);
          box-shadow: 0 8px 40px rgba(201,168,76,0.3);
          transform: translateY(-2px);
        }
      `}</style>

      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        padding: '120px 24px 80px',
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,168,76,0.08) 0%, transparent 70%)',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(201,168,76,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '24px' }}>
          Kenya's Premier Marketplace
        </p>

        <h1 style={{ lineHeight: 1, marginBottom: '24px' }}>
          <span className="brand-font" style={{ fontSize: 'clamp(64px, 12vw, 120px)', color: '#F0EDE8', display: 'block' }}>
            One <span style={{ color: 'var(--gold)' }}>Stop</span> Shop
          </span>
        </h1>

        <p className="serif" style={{ fontSize: 'clamp(16px, 2.5vw, 22px)', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 300 }}>
          Land · Houses · Cars · More
        </p>

        <div className="divider" style={{ margin: '24px auto' }} />

        <p style={{ color: 'var(--text-muted)', fontSize: '14px', maxWidth: '400px', lineHeight: 1.7, marginBottom: '48px' }}>
          Browse verified listings across Kenya. We connect serious buyers with genuine sellers — with full privacy until you're ready.
        </p>

        <button
          className="glass-btn"
          onClick={() => { setCenterClicked(!centerClicked); setActive(null); }}
        >
          {centerClicked ? 'Choose a Service' : 'Explore Services'}
        </button>

        {centerClicked && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '12px',
            justifyContent: 'center', marginTop: '40px', maxWidth: '800px',
            position: 'relative', zIndex: 10,
          }}>
            {services.map((service) => (
              <div key={service.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: '140px', zIndex: 10 }}>
                <button
                  onClick={() => { if (!service.route) return; setActive(active === service.id ? null : service.id); }}
                  style={{
                    background: active === service.id ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(201,168,76,0.15)',
                    padding: '12px 20px', borderRadius: '50px',
                    cursor: service.route ? 'pointer' : 'not-allowed',
                    color: service.route ? 'var(--gold)' : 'var(--text-muted)',
                    fontSize: '13px', fontWeight: 500,
                    width: '100%', transition: 'all 0.2s',
                    opacity: service.route ? 1 : 0.5,
                    position: 'relative', zIndex: 10,
                  }}
                >
                  {service.icon} {service.label}
                  {!service.route && <span style={{ display: 'block', fontSize: '10px', marginTop: '2px' }}>Coming Soon</span>}
                </button>

                {active === service.id && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', position: 'relative', zIndex: 20 }}>
                    {service.subs.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => { if (service.route) navigate(service.route); }}
                        style={{
                          background: 'rgba(201,168,76,0.05)',
                          border: '1px solid rgba(201,168,76,0.2)',
                          color: '#C9A84C',
                          padding: '10px 14px',
                          borderRadius: '50px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          textAlign: 'left',
                          width: '100%',
                          position: 'relative',
                          zIndex: 20,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.15)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.05)'; }}
                      >
                        → {sub}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
          <StatCard label="Rentals Listed" target={stats.rentals} />
          <StatCard label="Happy Clients" target={50} />
          <StatCard label="Counties Covered" target={5} />
          <StatCard label="Cars Available" target={20} />
        </div>
      </div>

      {/* Services Grid */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px 120px' }}>
        <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '16px' }}>What We Offer</p>
        <h2 className="serif" style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: 'var(--text)', fontWeight: 300, marginBottom: '48px' }}>
          Everything you need,<br />in one place.
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {services.filter(s => s.route).map((service) => (
            <div key={service.id} className="card" style={{ padding: '32px', cursor: 'pointer' }}
              onClick={() => navigate(service.route!)}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>{service.icon}</div>
              <div className="divider" />
              <h3 style={{ color: 'var(--text)', fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>{service.label}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6 }}>
                {service.id === 'land' && 'Prime plots across Kenya — residential, agricultural, commercial and beach.'}
                {service.id === 'houses-sale' && 'Own your dream home. Browse verified properties from studios to mansions.'}
                {service.id === 'houses-rent' && 'Unlock exact locations on payment. Secure, private, and hassle-free.'}
                {service.id === 'cars' && 'Curated dealership listings. We personally take you for a test drive.'}
              </p>
              <p style={{ color: 'var(--gold)', fontSize: '12px', marginTop: '24px', letterSpacing: '0.06em' }}>View listings →</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;